
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const NavigationContext = createContext();

export const useNavigation = () => {
    const context = useContext(NavigationContext);
    if (!context) {
        throw new Error('useNavigation must be used within a NavigationProvider');
    }
    return context;
};

export const NavigationProvider = ({ children }) => {
    const [page, setPage] = useState('dashboard');
    const [pageProps, setPageProps] = useState({});

    // Mobile menu state (often coupled with navigation)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    // Initial load from hash and History handling
    useEffect(() => {
        // Helper to parse hash params
        const parseHash = (hashStr) => {
            const cleanHash = hashStr.replace('#', '');
            const [pageName, queryString] = cleanHash.split('?');

            const params = {};
            if (queryString) {
                const searchParams = new URLSearchParams(queryString);
                for (const [key, value] of searchParams.entries()) {
                    params[key] = value;
                }
            }
            return { pageName: pageName || 'dashboard', params };
        };

        const handlePopState = (event) => {
            // Restore state from history if available
            if (event.state && event.state.page) {
                setPage(event.state.page);
                setPageProps(event.state.props || {});
            } else {
                // Fallback to hash parsing
                const { pageName, params } = parseHash(window.location.hash);
                setPage(pageName);
                if (Object.keys(params).length > 0) {
                    setPageProps(prev => ({ ...prev, ...params }));
                }
            }
        };

        // Check initial hash
        const { pageName, params } = parseHash(window.location.hash);
        setPage(pageName);
        if (Object.keys(params).length > 0) {
            setPageProps(params);
        }

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const navigate = useCallback((newPage, props = {}) => {
        // Construct query string for persistent props (whitelist crucial IDs)
        // We don't want to dump large objects into URL, only IDs.
        const persistentKeys = ['rombelId', 'quizId', 'resultId', 'materiId', 'lkpdId'];
        const queryParams = new URLSearchParams();

        Object.keys(props).forEach(key => {
            if (persistentKeys.includes(key) && props[key]) {
                queryParams.append(key, props[key]);
            }
        });

        const queryString = queryParams.toString();
        const url = queryString ? `#${newPage}?${queryString}` : `#${newPage}`;

        if (page !== newPage || window.location.hash !== url) {
            window.history.pushState({ page: newPage, props }, '', url);
        }

        setPage(newPage);
        setPageProps(props);
        setIsMobileMenuOpen(false);
        window.scrollTo(0, 0);
    }, [page]);

    const goBack = useCallback(() => {
        window.history.back();
    }, []);

    const value = {
        page,
        pageProps,
        navigate, // This replaces setPage
        goBack,
        isMobileMenuOpen,
        setIsMobileMenuOpen,
        isSidebarCollapsed,
        setIsSidebarCollapsed
    };

    return (
        <NavigationContext.Provider value={value}>
            {children}
        </NavigationContext.Provider>
    );
};
